import { useEffect, useState } from 'react';
import {
  Box, Typography, Button, Paper, Chip, FormControl, InputLabel,
  Select, MenuItem, TextField, Dialog, DialogTitle, DialogContent,
  DialogActions, IconButton, Tooltip, Table, TableHead, TableBody,
  TableRow, TableCell, Grid
} from '@mui/material';
import { DataGrid, type GridColDef } from '@mui/x-data-grid';
import { Add, Visibility, Cancel } from '@mui/icons-material';
import { useMovimientoInventarioStore } from '../store/movimientoStore';
import { movimientoInventarioService } from '../services/movimientoService';
import type { MovimientoInventario, MovimientoRequest, MovimientoDetalleRequest } from '../types/movimiento.types';
import { almacenService } from '../../almacenes/services/almacenService';
import type { Deposito } from '../../almacenes/types/almacen.types';

const MovimientosPage = () => {
  const { movimientos, loading, filtroTipo, setMovimientos, setLoading, setFiltroTipo } = useMovimientoInventarioStore();
  const [depositos, setDepositos] = useState<Deposito[]>([]);
  const [openNew, setOpenNew] = useState(false);
  const [openDetail, setOpenDetail] = useState(false);
  const [selectedMov, setSelectedMov] = useState<MovimientoInventario | null>(null);
  const [formData, setFormData] = useState<MovimientoRequest>({
    nombre: '', idDeposito: null, idPersona: null, idSucursal: null,
    idComprobante: 1, tipoMovimiento: 'E', detalles: []
  });
  const [newDetalle, setNewDetalle] = useState<MovimientoDetalleRequest>({
    idArticulo: 0, idDeposito: 0, cantidad: 0, precioUnitario: 0
  });

  useEffect(() => { loadData(); loadDepositos(); }, []);
  useEffect(() => { loadData(); }, [filtroTipo]);

  const loadDepositos = async () => {
    try { setDepositos(await almacenService.getAll()); } catch { /* handled */ }
  };

  const loadData = async () => {
    setLoading(true);
    try {
      const data = await movimientoInventarioService.getAll(filtroTipo ? { tipo: filtroTipo } : undefined);
      setMovimientos(data);
    } finally { setLoading(false); }
  };

  const handleViewDetail = async (id: number) => {
    try {
      const detail = await movimientoInventarioService.getById(id);
      setSelectedMov(detail);
      setOpenDetail(true);
    } catch { /* handled */ }
  };

  const handleCreate = async () => {
    try {
      await movimientoInventarioService.create(formData);
      setOpenNew(false);
      setFormData({ nombre: '', idDeposito: null, idPersona: null, idSucursal: null, idComprobante: 1, tipoMovimiento: 'E', detalles: [] });
      await loadData();
    } catch { /* handled */ }
  };

  const handleAnular = async (id: number) => {
    if (!confirm('¿Anular este movimiento? Se revertirán los cambios de stock.')) return;
    try { await movimientoInventarioService.anular(id); await loadData(); } catch { /* handled */ }
  };

  const addDetalle = () => {
    if (newDetalle.idArticulo <= 0 || newDetalle.cantidad <= 0) return;
    setFormData(prev => ({ ...prev, detalles: [...prev.detalles, { ...newDetalle }] }));
    setNewDetalle({ idArticulo: 0, idDeposito: formData.idDeposito ?? 0, cantidad: 0, precioUnitario: 0 });
  };

  const columns: GridColDef[] = [
    { field: 'idMovimiento', headerName: 'ID', width: 70 },
    { field: 'nombre', headerName: 'Descripción', flex: 1, minWidth: 200 },
    { field: 'fechaMovimiento', headerName: 'Fecha', width: 120,
      valueFormatter: (value) => value ? new Date(value).toLocaleDateString() : '' },
    { field: 'nombreDeposito', headerName: 'Almacén', width: 150 },
    { field: 'estado', headerName: 'Estado', width: 100,
      renderCell: (params) => <Chip label={params.value === 'V' ? 'Vigente' : 'Anulado'} size="small" color={params.value === 'V' ? 'success' : 'error'} />
    },
    { field: 'actions', headerName: 'Acciones', width: 120, sortable: false,
      renderCell: (params) => (
        <Box>
          <Tooltip title="Ver detalle"><IconButton size="small" onClick={() => handleViewDetail(params.row.idMovimiento)}><Visibility fontSize="small" /></IconButton></Tooltip>
          {params.row.estado === 'V' && (
            <Tooltip title="Anular"><IconButton size="small" color="error" onClick={() => handleAnular(params.row.idMovimiento)}><Cancel fontSize="small" /></IconButton></Tooltip>
          )}
        </Box>
      )
    },
  ];

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h5" fontWeight="bold">Movimientos de Inventario</Typography>
        <Button variant="contained" startIcon={<Add />} onClick={() => setOpenNew(true)}>Nuevo Movimiento</Button>
      </Box>

      <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Tipo</InputLabel>
          <Select value={filtroTipo} label="Tipo" onChange={(e) => setFiltroTipo(e.target.value)}>
            <MenuItem value="">Todos</MenuItem>
            <MenuItem value="E">Entrada</MenuItem>
            <MenuItem value="S">Salida</MenuItem>
            <MenuItem value="T">Transferencia</MenuItem>
          </Select>
        </FormControl>
      </Box>

      <Paper sx={{ height: 500 }}>
        <DataGrid rows={movimientos} columns={columns} getRowId={(row) => row.idMovimiento}
          loading={loading} density="compact"
          initialState={{ pagination: { paginationModel: { pageSize: 25 } } }} pageSizeOptions={[25, 50]} />
      </Paper>

      {/* New Movement Dialog */}
      <Dialog open={openNew} onClose={() => setOpenNew(false)} maxWidth="md" fullWidth>
        <DialogTitle>Nuevo Movimiento</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid size={{ xs: 12, sm: 4 }}>
              <FormControl fullWidth size="small">
                <InputLabel>Tipo</InputLabel>
                <Select value={formData.tipoMovimiento} label="Tipo"
                  onChange={(e) => setFormData({ ...formData, tipoMovimiento: e.target.value })}>
                  <MenuItem value="E">Entrada</MenuItem>
                  <MenuItem value="S">Salida</MenuItem>
                  <MenuItem value="T">Transferencia</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, sm: 8 }}>
              <TextField fullWidth size="small" label="Descripción" value={formData.nombre}
                onChange={(e) => setFormData({ ...formData, nombre: e.target.value })} />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <FormControl fullWidth size="small">
                <InputLabel>Almacén Origen</InputLabel>
                <Select value={formData.idDeposito ?? ''} label="Almacén Origen"
                  onChange={(e) => setFormData({ ...formData, idDeposito: Number(e.target.value) })}>
                  {depositos.map((d) => <MenuItem key={d.idDeposito} value={d.idDeposito}>{d.nombre}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            {formData.tipoMovimiento === 'T' && (
              <Grid size={{ xs: 12, sm: 6 }}>
                <FormControl fullWidth size="small">
                  <InputLabel>Almacén Destino</InputLabel>
                  <Select value={formData.idDepositoDestino ?? ''} label="Almacén Destino"
                    onChange={(e) => setFormData({ ...formData, idDepositoDestino: Number(e.target.value) })}>
                    {depositos.map((d) => <MenuItem key={d.idDeposito} value={d.idDeposito}>{d.nombre}</MenuItem>)}
                  </Select>
                </FormControl>
              </Grid>
            )}
            <Grid size={12}>
              <TextField fullWidth size="small" label="Observaciones" multiline rows={2}
                value={formData.observaciones ?? ''}
                onChange={(e) => setFormData({ ...formData, observaciones: e.target.value })} />
            </Grid>
          </Grid>

          <Typography variant="subtitle1" sx={{ mt: 3, mb: 1, fontWeight: 'bold' }}>Detalle</Typography>
          <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
            <TextField size="small" label="ID Artículo" type="number" value={newDetalle.idArticulo || ''}
              onChange={(e) => setNewDetalle({ ...newDetalle, idArticulo: Number(e.target.value) })} sx={{ width: 120 }} />
            <TextField size="small" label="Cantidad" type="number" value={newDetalle.cantidad || ''}
              onChange={(e) => setNewDetalle({ ...newDetalle, cantidad: Number(e.target.value) })} sx={{ width: 120 }} />
            <TextField size="small" label="Precio Unit." type="number" value={newDetalle.precioUnitario || ''}
              onChange={(e) => setNewDetalle({ ...newDetalle, precioUnitario: Number(e.target.value) })} sx={{ width: 120 }} />
            <Button variant="outlined" onClick={addDetalle}>Agregar</Button>
          </Box>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Artículo</TableCell>
                <TableCell align="right">Cantidad</TableCell>
                <TableCell align="right">Precio</TableCell>
                <TableCell align="right">Importe</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {formData.detalles.map((d, i) => (
                <TableRow key={i}>
                  <TableCell>Art. #{d.idArticulo}</TableCell>
                  <TableCell align="right">{d.cantidad}</TableCell>
                  <TableCell align="right">{d.precioUnitario}</TableCell>
                  <TableCell align="right">{(d.cantidad * d.precioUnitario).toFixed(2)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenNew(false)}>Cancelar</Button>
          <Button variant="contained" onClick={handleCreate} disabled={formData.detalles.length === 0}>Guardar</Button>
        </DialogActions>
      </Dialog>

      {/* Detail Dialog */}
      <Dialog open={openDetail} onClose={() => setOpenDetail(false)} maxWidth="md" fullWidth>
        <DialogTitle>Detalle del Movimiento #{selectedMov?.idMovimiento}</DialogTitle>
        <DialogContent>
          {selectedMov && (
            <>
              <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
                <Typography><strong>Descripción:</strong> {selectedMov.nombre}</Typography>
                <Typography><strong>Almacén:</strong> {selectedMov.nombreDeposito}</Typography>
                <Typography><strong>Fecha:</strong> {new Date(selectedMov.fechaMovimiento).toLocaleDateString()}</Typography>
                <Chip label={selectedMov.estado === 'V' ? 'Vigente' : 'Anulado'} size="small" color={selectedMov.estado === 'V' ? 'success' : 'error'} />
              </Box>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Código</TableCell>
                    <TableCell>Artículo</TableCell>
                    <TableCell>U.M.</TableCell>
                    <TableCell align="right">Cantidad</TableCell>
                    <TableCell align="right">Precio</TableCell>
                    <TableCell align="right">Importe</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {selectedMov.detalles?.map((d, i) => (
                    <TableRow key={i}>
                      <TableCell>{d.codigoArticulo}</TableCell>
                      <TableCell>{d.descripcionArticulo}</TableCell>
                      <TableCell>{d.unidadMedida}</TableCell>
                      <TableCell align="right">{d.cantidad}</TableCell>
                      <TableCell align="right">{d.precioUnitario?.toFixed(2)}</TableCell>
                      <TableCell align="right">{d.importe?.toFixed(2)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </>
          )}
        </DialogContent>
        <DialogActions><Button onClick={() => setOpenDetail(false)}>Cerrar</Button></DialogActions>
      </Dialog>
    </Box>
  );
};

export default MovimientosPage;
